import React from 'react';
import { useDispatch } from 'react-redux';
import { Draggable, Droppable, type DraggableProvided, type DraggableStateSnapshot } from 'react-beautiful-dnd';
import { FixedSizeList as VirtualList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Chip, Typography } from '@mui/material';

import useSettings from 'app/main/components/hooks/useSettings';
import Icon from 'app/main/components/Icon';
import Text from 'app/main/components/Text';
import { showFastAddDialog } from 'app/main/modules/components/FastAddDialog';
import KanbanCardManager from 'app/main/modules/components/KanbanCardManager';
import { type ViewItemButtonProps } from '../../ViewPage';

export interface BoardCardLayoutProps {
	model: any;
	buttonProps: ViewItemButtonProps;
	provided: DraggableProvided;
	snapshot: DraggableStateSnapshot;
}

type BoardCardProps = {
	module: string;
	updateRefresh: () => void;
} & Omit<BoardCardLayoutProps, 'buttonProps'>;

export interface BoardListType {
	id: string;
	name: string;
	description?: string;
	color?: string;
	cards?: any[];
}

interface VirtualListItemProps {
	data: {
		module: string;
		cards: any[];
		updateRefresh: () => void;
	};
	index: number;
	style?: React.CSSProperties;
}

interface BoardListProps {
	module: string;
	list: BoardListType;
	baseModel?: any;
	draggedCard?: any;
	updateRefresh: () => void;
}

const BoardCard = ({ module, model, updateRefresh, provided, snapshot }: BoardCardProps) => {
	const { settings } = useSettings(module);

	const CardLayout: React.FC<BoardCardLayoutProps> = KanbanCardManager[module];
	const buttonProps: ViewItemButtonProps = { settings, model, updateRefresh };

	return <CardLayout model={model} buttonProps={buttonProps} provided={provided} snapshot={snapshot} />;
};

const VirtualListItem = React.memo(({ data, index, style }: VirtualListItemProps) => {
	const { module, cards, updateRefresh } = data;
	const { settings } = useSettings(module);

	const model = cards[index];
	const draggableId = model[`${settings.modu_short}_uid`];

	return (
		<div style={style}>
			<Draggable draggableId={draggableId} index={index}>
				{(provided, snapshot) => (
					<BoardCard
						module={module}
						model={model}
						updateRefresh={updateRefresh}
						provided={provided}
						snapshot={snapshot}
					/>
				)}
			</Draggable>
		</div>
	);
});

export default function BoardList({ module, list, baseModel, draggedCard, updateRefresh }: BoardListProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const handleNewCard = () => {
		dispatch(showFastAddDialog({ module, model: { ...(baseModel || []), acti_acst_id: list.id }, updateRefresh }));
	};

	return (
		<Card
			className="mr-16 flex h-full w-256 flex-col last:mr-0 sm:w-360"
			variant="outlined"
			sx={{ borderTop: list.color ? `4px solid ${list.color}` : undefined }}
		>
			<CardHeader
				title={<Text className="font-600">{list.name}</Text>}
				subheader={<Text color="text.secondary">{list.description}</Text>}
				action={<Chip label={list.cards?.length} size="small" />}
			/>
			<Droppable
				droppableId={`${list.id}`}
				mode="virtual"
				type="card"
				direction="vertical"
				renderClone={(provided, snapshot, rubric) =>
					draggedCard && (
						<BoardCard
							module={module}
							model={draggedCard}
							updateRefresh={updateRefresh}
							provided={provided}
							snapshot={snapshot}
						/>
					)
				}
			>
				{(provided, snapshot) => (
					<CardContent {...provided.droppableProps} ref={provided.innerRef} className="h-full p-0">
						{snapshot.isDraggingOver && (
							<Box className="h-full w-full p-8">
								<Box
									className="flex h-full w-full flex-col items-center justify-center rounded-8 border-2 border-dashed"
									sx={theme => ({ borderColor: theme.palette.divider })}
								>
									<Icon icon="tray-plus" size={64} color="text.secondary" />
									<Typography variant="subtitle2" color="text.secondary">
										Rilascia per spostare in "{list.name}"
									</Typography>
								</Box>
							</Box>
						)}
						<AutoSizer style={{ display: snapshot.isDraggingOver ? 'none' : undefined }}>
							{({ width, height }) => (
								<VirtualList
									width={width}
									height={height}
									itemData={{ module, cards: list.cards || [], updateRefresh }}
									itemCount={list.cards?.length || 0}
									itemSize={150}
								>
									{VirtualListItem}
								</VirtualList>
							)}
						</AutoSizer>
					</CardContent>
				)}
			</Droppable>
			<CardActions className="p-0">
				<Button className="h-48 w-full rounded-none font-600 normal-case" onClick={handleNewCard}>
					<Icon className="text-20" icon="add-circle-outline" color="inherit" />
					<span className="mx-8">{t('DIALOG_ACTION_ADD')}</span>
				</Button>
			</CardActions>
		</Card>
	);
}
